import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash,
  Anchor,
  MagnifyingGlass,
  Spinner,
  Phone,
  Envelope,
  MapPin,
  Globe
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TERRITORIES = [
  'Caribbean', 'North America', 'South America', 'Europe', 'Asia', 'Other'
];

const COMMON_PORTS = [
  'Miami', 'Fort Lauderdale', 'Houston', 'New York', 'Bridgetown', 'Kingston',
  'Port of Spain', 'Castries', 'St. Johns', 'Nassau', 'San Juan', 'Other'
];

const FreightForwardersPage = () => {
  const { getAuthHeaders } = useAuth();
  const [forwarders, setForwarders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFF, setEditingFF] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    territories_served: [],
    ports_served: []
  });

  useEffect(() => {
    fetchForwarders();
  }, []);

  const fetchForwarders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/freight-forwarders`, getAuthHeaders());
      setForwarders(response.data);
    } catch (error) {
      toast.error('Failed to load freight forwarders');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingFF) {
        await axios.put(`${API_URL}/api/freight-forwarders/${editingFF.id}`, formData, getAuthHeaders());
        toast.success('Freight forwarder updated successfully');
      } else {
        await axios.post(`${API_URL}/api/freight-forwarders`, formData, getAuthHeaders());
        toast.success('Freight forwarder created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchForwarders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save freight forwarder');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (ff) => {
    if (!confirm(`Are you sure you want to deactivate ${ff.company_name}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/freight-forwarders/${ff.id}`, getAuthHeaders());
      toast.success('Freight forwarder deactivated');
      fetchForwarders();
    } catch (error) {
      toast.error('Failed to deactivate freight forwarder');
    }
  };

  const openEdit = (ff) => {
    setEditingFF(ff);
    setFormData({
      company_name: ff.company_name,
      contact_person: ff.contact_person,
      email: ff.email,
      phone: ff.phone,
      territories_served: ff.territories_served || [],
      ports_served: ff.ports_served || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingFF(null);
    setFormData({
      company_name: '',
      contact_person: '',
      email: '',
      phone: '',
      territories_served: [],
      ports_served: []
    });
  };

  const toggleTerritory = (territory) => {
    setFormData(prev => ({
      ...prev,
      territories_served: prev.territories_served.includes(territory)
        ? prev.territories_served.filter(t => t !== territory)
        : [...prev.territories_served, territory]
    }));
  };

  const togglePort = (port) => {
    setFormData(prev => ({
      ...prev,
      ports_served: prev.ports_served.includes(port)
        ? prev.ports_served.filter(p => p !== port)
        : [...prev.ports_served, port]
    }));
  };

  const filteredForwarders = forwarders.filter(ff =>
    ff.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ff.territories_served?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase())) ||
    ff.ports_served?.some(p => p.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F52BA] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Freight Forwarders
          </h1>
          <p className="text-slate-500 mt-1">Manage your freight forwarder registry</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="new-ff-btn">
              <Plus size={20} className="mr-2" />
              Add Freight Forwarder
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
                {editingFF ? 'Edit Freight Forwarder' : 'Add New Freight Forwarder'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Caribbean Cargo Ltd"
                    required
                    data-testid="ff-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Person</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                    placeholder="John Smith"
                    required
                    data-testid="ff-contact-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@cargo.com"
                    required
                    data-testid="ff-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1-268-555-0100"
                    required
                    data-testid="ff-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Territories Served</Label>
                <div className="flex flex-wrap gap-2">
                  {TERRITORIES.map(territory => (
                    <button
                      key={territory}
                      type="button"
                      onClick={() => toggleTerritory(territory)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.territories_served.includes(territory)
                          ? 'bg-[#0F52BA] text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`territory-${territory.toLowerCase().replace(/ /g, '-')}`}
                    >
                      {territory}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Ports Served</Label>
                <div className="flex flex-wrap gap-2">
                  {COMMON_PORTS.map(port => (
                    <button
                      key={port}
                      type="button"
                      onClick={() => togglePort(port)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.ports_served.includes(port)
                          ? 'bg-[#38B2AC] text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`port-${port.toLowerCase().replace(/ /g, '-')}`}
                    >
                      {port}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <Button type="button" variant="secondary" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="save-ff-btn">
                  {submitting ? <Spinner size={20} className="animate-spin mr-2" /> : null}
                  {editingFF ? 'Update Freight Forwarder' : 'Add Freight Forwarder'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search freight forwarders..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="ff-search-input"
        />
      </div>

      {/* Forwarders grid */}
      {filteredForwarders.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-16 text-center">
          <Anchor size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No freight forwarders found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredForwarders.map(ff => (
            <FreightForwarderCard
              key={ff.id}
              forwarder={ff}
              onEdit={() => openEdit(ff)}
              onDelete={() => handleDelete(ff)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FreightForwarderCard = ({ forwarder, onEdit, onDelete }) => {
  return (
    <div 
      className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 card-hover"
      data-testid={`ff-card-${forwarder.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-[#0F52BA]/10 flex items-center justify-center">
          <Anchor size={24} weight="duotone" className="text-[#0F52BA]" />
        </div>
        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className="p-2 rounded-lg text-slate-400 hover:text-[#0F52BA] hover:bg-[#F0F7FF] transition-colors"
            data-testid={`edit-ff-${forwarder.id}`}
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-lg text-slate-400 hover:text-[#E63946] hover:bg-red-50 transition-colors"
            data-testid={`delete-ff-${forwarder.id}`}
          >
            <Trash size={18} />
          </button>
        </div>
      </div>

      <h3 className="font-semibold text-slate-800 mb-1">{forwarder.company_name}</h3>
      <p className="text-sm text-slate-500 mb-4">{forwarder.contact_person}</p>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-slate-500">
          <Phone size={16} />
          <span>{forwarder.phone}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-500">
          <Envelope size={16} />
          <span className="truncate">{forwarder.email}</span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100">
        {/* Territories */}
        {forwarder.territories_served && forwarder.territories_served.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-slate-500 mb-1.5 flex items-center gap-1">
              <Globe size={12} /> Territories
            </p>
            <div className="flex flex-wrap gap-1">
              {forwarder.territories_served.slice(0, 3).map(territory => (
                <span key={territory} className="px-2 py-0.5 bg-[#0F52BA]/10 rounded text-xs text-[#0F52BA]">
                  {territory}
                </span>
              ))}
              {forwarder.territories_served.length > 3 && (
                <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                  +{forwarder.territories_served.length - 3}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Ports */}
        {forwarder.ports_served && forwarder.ports_served.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 mb-1.5 flex items-center gap-1">
              <MapPin size={12} /> Ports
            </p>
            <div className="flex flex-wrap gap-1">
              {forwarder.ports_served.slice(0, 3).map(port => (
                <span key={port} className="px-2 py-0.5 bg-[#38B2AC]/10 rounded text-xs text-[#38B2AC]">
                  {port}
                </span>
              ))}
              {forwarder.ports_served.length > 3 && (
                <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                  +{forwarder.ports_served.length - 3}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreightForwardersPage;
